import { useEffect, useState, type ReactNode } from "react";
import { api, getViewingOrgId } from "../../api/client";
import { displayControlRef } from "../../lib/controlRef";
import { useFrameworks } from "../../context/FrameworksContext";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { theme, type as text } from "../../theme";
import { SeverityPill, MultiPick, Field, input, type Option } from "../resilience/shared";

// Shared pieces of the management system registers (docs/MANAGEMENT-SYSTEM.md). The rules —
// significance, risk bands, objective standing, required review inputs — are computed by the API
// (backend/src/lib/managementSystem.ts) and arrive on each row; this file only displays them.

/** Mirrors backend MS_FRAMEWORK_IDS / MS_FRAMEWORK_LABELS. */
export const MS_FRAMEWORKS = ["iso27001", "iso9001", "iso42001", "iso45001", "iso14001"] as const;
export type MsFrameworkId = (typeof MS_FRAMEWORKS)[number];
export const MS_LABELS: Record<MsFrameworkId, string> = {
  iso27001: "ISO/IEC 27001", iso9001: "ISO 9001", iso42001: "ISO/IEC 42001", iso45001: "ISO 45001", iso14001: "ISO 14001",
};

/** Mirrors backend MS_REF_PREFIX. */
const PREFIX = { context: "CTX", objective: "OBJ", audit: "IA", review: "MR", competence: "CMP", aspect: "ENV", hazard: "HAZ", obligation: "OBL" } as const;
export type MsRefKind = keyof typeof PREFIX;
export const msRef = (kind: MsRefKind, displayId: number | null | undefined) =>
  displayId != null ? `${PREFIX[kind]}-${String(displayId).padStart(3, "0")}` : PREFIX[kind];

export const words = (s: string | null | undefined) => (s ?? "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export interface Person { id: string; displayName: string | null; email: string; isActive: boolean }
export const personName = (p: Person | null | undefined, name: string | null | undefined) =>
  p ? `${p.displayName || p.email}${p.isActive ? "" : " (deactivated)"}` : name || "—";

export interface AttentionItem {
  id: string; type: string; title: string; description: string; href: string;
  severity: "high" | "medium" | "low"; dueAt: string | null; frameworkId?: string | null;
}

/** The management-system standards the organisation is enrolled in. */
export function useMsEnrolment(): MsFrameworkId[] {
  const { enrolled } = useFrameworks();
  return MS_FRAMEWORKS.filter((f) => (enrolled as ReadonlySet<string>).has(f));
}

/** Who may write: every register needs controls:write, the internal audit routes audit:write. */
export function useMsPermissions() {
  const { user } = useCurrentUser();
  const perms = user?.permissions ?? [];
  return { canWrite: perms.includes("controls:write"), canAudit: perms.includes("audit:write") };
}

// Keyed by the organisation being looked at, so moving to another client in the same tab never offers
// the previous client's people (the consultant read-through and the group switcher change it without a reload).
let membersCache: { org: string; options: Option[] } | null = null;
const currentOrgKey = () => getViewingOrgId() ?? "own";
/** Active members, for owner / chair / auditor pickers. */
export function useMembers(): Option[] {
  const org = currentOrgKey();
  const [members, setMembers] = useState<Option[]>(membersCache?.org === org ? membersCache.options : []);
  useEffect(() => {
    if (membersCache?.org === org) { setMembers(membersCache.options); return; }
    setMembers([]);
    let live = true;
    api.get<{ members: Array<{ id: string; displayName: string | null; email: string; isActive?: boolean }> }>("/organizations/members")
      .then((d) => {
        const options = d.members.filter((m) => m.isActive !== false).map((m) => ({ id: m.id, name: m.displayName || m.email }));
        membersCache = { org, options };
        if (live) setMembers(options);
      })
      .catch(() => { /* a viewer without access still sees the register */ });
    return () => { live = false; };
  }, [org]);
  return members;
}

/**
 * A clause or control as a person reads it: the standard's own reference, with the standard named
 * ("ISO 45001 6.1.2"), never the platform's internal key ("iso45001-6.1.2"). ISO 27001's ids are
 * already the standard's references ("9.2", "A.5.1").
 */
export function clauseLabel(id: string): string {
  const m = id.match(/^(iso\d+)-/i);
  const fw = m ? m[1].toLowerCase() : /^(A\.)?\d+(\.\d+)*$/.test(id) ? "iso27001" : null;
  const std = fw ? MS_LABELS[fw as MsFrameworkId] : null;
  const ref = displayControlRef(id);
  return std ? `${std} ${ref}` : ref;
}

/** An audit's clauses, grouped by standard: "ISO 45001 6.1.2, 7.2 · ISO 9001 8.7". */
export function clausesByStandard(ids: readonly string[]): string {
  const groups = new Map<string, string[]>();
  for (const id of ids) {
    const label = clauseLabel(id);
    const ref = displayControlRef(id);
    const std = label.endsWith(` ${ref}`) ? label.slice(0, label.length - ref.length - 1) : "";
    (groups.get(std) ?? groups.set(std, []).get(std)!).push(ref);
  }
  return [...groups.entries()].map(([std, refs]) => `${std ? `${std} ` : ""}${refs.join(", ")}`).join(" · ");
}

/** Which standards a row serves. None ticked means every one the organisation is enrolled in. */
export function FrameworkPick({ allowed, value, onChange }: { allowed: readonly MsFrameworkId[]; value: string[]; onChange: (ids: string[]) => void }) {
  if (allowed.length <= 1) return null;
  return (
    <Field label="Standards" span2 help="Leave all unticked when it serves every management-system standard you hold.">
      <MultiPick options={allowed.map((id) => ({ id, name: MS_LABELS[id] }))} selected={value} onChange={onChange} />
    </Field>
  );
}

/** An owner who is a member, or a name for someone who is not. */
export function OwnerField({ label = "Owner", userId, name, onChange, members }: {
  label?: string; userId: string; name: string; members: Option[];
  onChange: (next: { userId: string; name: string }) => void;
}) {
  return (
    <Field label={label} help="A member of the organisation, or type a name for someone who is not.">
      <div style={{ display: "flex", gap: 6 }}>
        <select style={{ ...input, flex: 1 }} value={userId} onChange={(e) => onChange({ userId: e.target.value, name: e.target.value ? "" : name })}>
          <option value="">Not a member</option>
          {userId && !members.some((m) => m.id === userId) && <option value={userId}>Current</option>}
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        {!userId && <input style={{ ...input, flex: 1 }} placeholder="Name" value={name} onChange={(e) => onChange({ userId: "", name: e.target.value })} />}
      </div>
    </Field>
  );
}

/** The attention items for one register, most severe first. */
export function AttentionList({ items, empty }: { items: AttentionItem[]; empty?: string }) {
  if (!items.length) return empty ? <p style={{ ...text.hint, margin: "0 0 12px" }}>{empty}</p> : null;
  const order = { high: 0, medium: 1, low: 2 } as const;
  return (
    <ul style={{ listStyle: "none", margin: "0 0 14px", padding: 0, display: "grid", gap: 6 }}>
      {[...items].sort((a, b) => order[a.severity] - order[b.severity]).map((a) => (
        <li key={a.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", border: "1px solid var(--c-border)", borderRadius: 3, background: "var(--c-panel)" }}>
          <SeverityPill value={a.severity === "low" ? "suggested" : a.severity} />
          <div style={{ minWidth: 0 }}>
            <a href={a.href} style={{ ...text.itemTitle, fontSize: 13, color: theme.color.ref, textDecoration: "none" }}>{a.title}</a>
            <div style={text.hint}>{a.description}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** A small key / value line under a row title. */
export function Meta({ children }: { children: ReactNode }) {
  return <div style={{ ...text.hint, display: "flex", flexWrap: "wrap", gap: "2px 14px" }}>{children}</div>;
}

/** The standards a row serves, as plain text. */
export const standardsOf = (ids: string[], enrolled: readonly MsFrameworkId[]) =>
  (ids.length ? ids : enrolled).map((f) => MS_LABELS[f as MsFrameworkId] ?? f).join(", ");

export const REVIEW_INTERVALS = [3, 6, 12, 24, 36] as const;

/** A row's attention items, found by the id the API gives them. */
export const attentionFor = (items: AttentionItem[], prefix: string) => items.filter((a) => a.type.startsWith(prefix));
